
<!--
  splitCard.vue
  Airwallex Payment Demo - Vue.  Created by Chao Ding and Josie Ku.
  airwallex-payment-elements Split Card element integration in Vue.
  Comments with "Example" demonstrate how states can be integrated
  with the element, they can be removed.
  Detailed guidance here: https://github.com/airwallex/airwallex-payment-demo/blob/master/docs/splitcard.md
-->
<template>
  <el-dialog
    title="Add a New Card"
    :visible.sync="dialogVisible"
    width="500px"
    :before-close="cancel"
    :close-on-click-modal="false"
    >
    <div id="card-container">
      <!--
        STEP #3a: Add empty containers for the split elements to be injected into
        - Ensure these are the only elements in your document with these id, otherwise the elements may fail to mount.
      -->
      <div class="field-container">
        <div class="field-label">{{ $t('PersonalCenter.Payment.Components.AirwallexCard.CardNumber') }}</div>
        <div id="cardNumber" />
        <p id="cardNumber-error" :style="{ color: 'red' }" />
      </div>
      <div class="field-container">
        <div class="field-label">{{ $t('PersonalCenter.Payment.Components.AirwallexCard.SecurityCode') }}(CVV)</div>
        <div id="cvc" />
        <p id="cvc-error" :style="{ color: 'red' }" />
      </div>
      <div class="field-container">
        <div class="field-label">{{ $t('PersonalCenter.Payment.Components.AirwallexCard.ExpireDate') }}</div>
        <div id="expiry" />
        <p id="expiry-error" :style="{ color: 'red' }" />
      </div>
      <!-- STEP #3b: Add a submit button to trigger the payment request -->
      <!-- <button id="confirm" @click="triggerConfirm">Confirm</button> -->
    </div>
    <div class="f--c terms">
      {{ $t('PersonalCenter.Payment.Components.AirwallexCard.AcceptTheTerms') }}
      <div class="right pointer" @click="jump">《Kakaclo Terms of Service》</div>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">Cancel</el-button>
      <el-button :disabled="isDisabled" :loading="loading" type="primary" @click="triggerConfirm">{{ $t('PersonalCenter.Payment.Components.AirwallexCard.Confirm') }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
// vue文档 https://github.com/airwallex/airwallex-payment-demo/blob/master/integrations/vue/src/components/splitCard.vue
import API from '@/api'
import { createElement, createPaymentConsent, loadAirwallex, getElement } from 'airwallex-payment-elements'

const elementsBlur = {
  cardNumber: false,
  expiry: false,
  cvc: false
}

export default {
  data() {
    return {
      dialogVisible: false,
      disabled: true,
      elementsBlur: { ...elementsBlur },
      loading: false,
      customer_id: '',
      client_secret: ''
    }
  },
  computed: {
    isDisabled() {
      return Object.values(this.elementsBlur).includes(false)
    }
  },
  methods: {
    init() {
      loadAirwallex({
        env: process.env.NODE_ENV === 'production' ? 'prod' : 'demo', // Setup which Airwallex env('staging' | 'demo' | 'prod') to integrate with
        origin: window.location.origin, // Setup your event target to receive the browser events message
        fonts: [
          // Customizes the font for the payment elements
          {
            src: 'https://checkout.airwallex.com/fonts/CircularXXWeb/CircularXXWeb-Regular.woff2',
            family: 'AxLLCircular',
            weight: 400
          }
        ]
        // For more detailed documentation at https://github.com/airwallex/airwallex-payment-demo/tree/master/docs#loadAirwallex
      }).then(() => {
        const cardNumEle = createElement('cardNumber')
        const cvcEle = createElement('cvc')
        const expiryEle = createElement('expiry')

        cardNumEle.mount('cardNumber') // This 'cardNumber' id MUST MATCH the id on your cardNumber empty container created in Step 3
        cvcEle.mount('cvc') // Same as above
        expiryEle.mount('expiry') // Same as above
      })
    },
    onBlur(event) {
      const { error, type } = event.detail
      this.elementsBlur[type] = true
      const element = document.getElementById(`${type}-error`)
      if (element && error) {
        element.innerHTML = error.message || JSON.stringify(error) // Example: set input error message
        this.elementsBlur[type] = false
      } else element.innerHTML = ''
    },
    triggerConfirm() {
      this.loading = true
      const cardNumElement = getElement('cardNumber')
      createPaymentConsent({
        element: cardNumElement, // Only need to submit CardNumber element
        customer_id: this.customer_id,
        client_secret: this.client_secret,
        currency: 'USD',
        next_triggered_by: 'merchant'
      }).then((res) => {
        this.dialogVisible = false
        this.addPaymentPaymentConsent(res)
      }).catch((error) => {
        this.dialogVisible = false
        this.$message.error(error.message)
      }).finally(() => {
        this.loading = false
      })
    },
    show() {
      this.dialogVisible = true
      this.elementsBlur = { ...elementsBlur }
      if (window.Airwallex) {
        window.Airwallex.destroyElement('dropIn')
        window.Airwallex = undefined
      }
      this.getAirwallexClientSecret()
      this.$nextTick(() => {
        this.init()
        const cardNumberElement = document.getElementById('cardNumber')
        const cvcElement = document.getElementById('cvc')
        const expiryElement = document.getElementById('expiry')
        cardNumberElement.addEventListener('onBlur', this.onBlur)
        cvcElement.addEventListener('onBlur', this.onBlur)
        expiryElement.addEventListener('onBlur', this.onBlur)
      })
    },
    // 获取用户clientSecret
    getAirwallexClientSecret() {
      API.getAirwallexClientSecret().then(res => {
        this.customer_id = res.data.customer_id
        this.client_secret = res.data.client_secret
      })
    },
    // 新增付款意向
    addPaymentPaymentConsent(res) {
      API.addPaymentPaymentConsent(res).then(res => {
        this.$message.success('The card has been added')
        this.$emit('success')
      })
    },
    cancel() {
      this.dialogVisible = false
      document.getElementById('cardNumber-error').innerHTML = ''
      document.getElementById('cvc-error').innerHTML = ''
      document.getElementById('expiry-error').innerHTML = ''
    },
    jump() {
      window.open('/terms-conditions')
    }
  },
  beforeDestroy() {
    const cardNumberElement = document.getElementById('cardNumber')
    const cvcElement = document.getElementById('cvc')
    const expiryElement = document.getElementById('expiry')
    if (cardNumberElement) cardNumberElement.removeEventListener('onBlur', this.onBlur)
    if (cvcElement) cvcElement.removeEventListener('onBlur', this.onBlur)
    if (expiryElement) expiryElement.removeEventListener('onBlur', this.onBlur)
  }
}
</script>

<style lang="scss" scoped>
.terms{
  margin-left: 50px;
  /deep/ .el-checkbox__label{
    color: unset;
  }
  .right {
    color: #99C41C;
    text-decoration: underline;
  }
}

.field-container {
  margin: 0 auto 16px;
  width: 80%;
}
.field-container div {
  border: 1px solid;
  padding: 4px 8px;
  margin-top: 6px;
}
div.field-label {
  text-align: left;
  border: 0;
}
/* Custom styling for the inputs */
#cardNumber,
#cvc,
#expiry {
  border: 1px solid #CDD4DD;
  border-radius: 5px;
  padding: 5px 10px;
  margin-top: 8px;
  height: 40px;
}
</style>
